import Promise from 'promise-polyfill';

import getConfig from './get-config/get-config';
import exposeMethods from './expose-methods/expose-methods';
import { error as logError } from '../shared/logger';

import { loadDependencies, loadResources, loadScript, loadStyle } from './load/load';

/**
 * Initializes the Eval component by loading dependencies, styles, scripts, and resources,
 * then creates and registers the app instance.
 *
 * @param {Object} params - Initialization parameters
 * @param {string} params.elementId - DOM element ID where the component will be injected
 * @param {string} params.treeId - The user's tree ID
 * @param {string} params.personId - The specific person ID from the tree
 * @param {string} params.flowId - The specific flow panel to inject
 * @param {string} [params.hintId] - The hint ID for a record
 * @param {string} [params.eventType] - Event type to filter hints ('birth', 'marriage', 'death')
 * @param {string} [params.renderer] - Renderer type ('sidebar' or 'custom')
 * @param {string} [params.sourceGid] - The sourceGid for a record
 * @param {Object} [configOverrides] - Optional config overrides for local development
 * @returns {Promise<void>}
 */
function init({ elementId, treeId, personId, flowId, hintId, eventType, renderer, sourceGid }, configOverrides) {
	const config = getConfig(renderer, configOverrides);

	const urls = {
		css: `${config.hintsUiEvalCdnUrl}/app.css`,
		app: `${config.hintsUiEvalCdnUrl}/app.js`,
		resources: `${config.hintsUiEvalCdnUrl}/resources/resources.${config.locale}.json`
	};

	return loadDependencies(config).then(() => {
		return Promise.all([
			loadStyle(urls.css),
			loadScript(urls.app),
			loadResources(urls.resources)
		]).then(response => {
			const { hintsEval } = window;
			const options = {
				elementId,
				flowId,
				hintId,
				treeId,
				personId,
				sourceGid,
				config,
				resources: response[2],
				renderer
			};
			if (eventType) {
				options.eventType = eventType.toLowerCase();
			}
			const app = hintsEval.createApp(options);
			if (!Object.hasOwnProperty.call(hintsEval, 'instances')) {
				hintsEval.instances = {};
			}
			hintsEval.instances[elementId] = app;
			Object.assign(hintsEval, exposeMethods(hintsEval.instances));
		}).catch(logError);
	}).catch(logError);
}

export default init;
